import { mdiFeather, mdiViewCarouselOutline } from '@mdi/js'
import Icon from '@mdi/react'
import IconToggle from '@mui/icons-material/KeyboardArrowUp'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import CardList from 'components/CardList'
import { PPDialog } from 'components/PPDialog'
import { ComposerMediaContext } from 'components/ComposerMediaContext/context'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { Article, ARTICLE_TYPE, ContentItem, GIF_TYPE, MyFile, Photo, PHOTO_TYPE } from 'interfaces'
import { STOCK_PHOTO_TYPE } from 'interfaces/Content/StockContentTypes'
import { useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import {
  addArticlePosts,
  addComposerImages,
  addComposerVideo,
  addImagePosts,
  addMultipostVideoFiles,
  addVideoPosts,
  resetComposer,
  setComposerContent
} from 'services/compose'
import { ArticleBase } from 'services/compose/interfaces/ArticleBase'
import { composerMultiPostSelector } from 'services/compose/selectors'
import { clearContentSelection, removeContentFromSelection, selectAllContent } from 'services/content'
import { contentSelectionSelector } from 'services/content/selectors'
import { instanceOfMyFile } from 'services/content/util'
import { openComposerContentDialog } from 'services/ui/actions'
import { STOCK_VIDEO_TYPE, VIDEO_TYPE } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './ContentSelectionDrawer.pcss'
import MiniContentCard from './MiniContentCard'

const LIST_HEIGHT = 108

export function ContentSelectionDrawer({ className }: { className?: string }) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch: contextDispatch } = useContext(ComposerMediaContext)
  const navigate = useNavigate()
  const location = useLocation()
  const multiposts = useSelector(composerMultiPostSelector).posts
  const selection = useSelector(contentSelectionSelector)
  const items = Object.values(selection.items).sort((a, b) => a.order - b.order)
  const itemsOnPage = Object.values(selection.itemsOnPage)
  const [open, setOpen] = useState(items.length > 0)
  const [preview, setPreview] = useState<{ imageUrl?: string, videoUrl?: string } | null>(null)
  const prevItemsCount = useRef(items.length)

  const hasMultiposts = Object.keys(multiposts).length > 0

  useEffect(() => {
    // EXPL: open the drawer when the selection becomes non-empty. Comparing against the previous
    // count instead of checking for exactly one item keeps bulk actions ("select all") working —
    // those jump straight from 0 to N and would otherwise leave the drawer collapsed.
    if (prevItemsCount.current === 0 && items.length > 0) {
      setOpen(true)
    }
    prevItemsCount.current = items.length
  }, [items.length])

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const removeContent = (id: string) => {
    dispatch(removeContentFromSelection([id]))
  }

  const photos = items.filter(item => item.type === PHOTO_TYPE || item.type === STOCK_PHOTO_TYPE)
  const photosCount = photos.length
  const withCarouselAction = items.length > 1 && photosCount === items.length

  const onContentAddedToComposer = () => {
    dispatch(clearContentSelection())
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
  }

  const addCarouselPost = () => {
    const imageUrls = photos.map(photo => (photo as Photo).imageUrl || (photo as MyFile).url)
    if (imageUrls.length > 0) {
      dispatch(resetComposer())
      dispatch(addComposerImages({ items: imageUrls.map(url => ({ url })), contextDispatch }))
      onContentAddedToComposer()
    }
  }

  const selectAll = () => {
    dispatch(selectAllContent())
  }

  const allSelected = !itemsOnPage.some(item => selection.items[item.id] === undefined)

  const addMultiPost = () => {
    const articles: Array<{ article: ArticleBase, content?: ContentItem }> = []
    const images: Array<{ url: string, content?: ContentItem }> = []
    const videos: ContentItem[] = []
    const videoFiles: Array<{ name?: string, url: string, thumbnailUrl?: string }> = []

    items.forEach(item => {
      if (item.type === ARTICLE_TYPE) {
        const article = item as Article
        articles.push({
          article: {
            imageUrl: article.imageUrl,
            title: article.title,
            url: article.sourceLink,
            description: article.description
          },
          content: article
        })
        return
      }
      if (instanceOfMyFile(item)) {
        if (item.type === PHOTO_TYPE || item.type === GIF_TYPE) {
          images.push({ url: item.url })
        } else if ((item as MyFile).type === VIDEO_TYPE) {
          videoFiles.push({ url: item.url, name: item.name, thumbnailUrl: item.thumbUrl })
        }
        return
      }
      if (item.type === STOCK_VIDEO_TYPE || item.type === VIDEO_TYPE) {
        videos.push(item as ContentItem)
        return
      }
      images.push({ url: item.imageUrl, content: item })
    })
    const count = images.length + articles.length + videos.length + videoFiles.length
    if (count > 0) {
      if (!hasMultiposts) {
        dispatch(resetComposer())
      }
      if (count === 1) {
        const content = articles[0]?.content || images[0]?.content || videos[0]
        if (content) {
          dispatch(setComposerContent({ content }))
        } else if (images[0] && images[0].url) {
          dispatch(addComposerImages({ items: [{ url: images[0].url }], contextDispatch }))
        } else if (videoFiles[0]) {
          dispatch(addComposerVideo({
            video: { url: videoFiles[0].url, name: videoFiles[0].name || '' },
            contextDispatch
          }))
        }
      } else {
        dispatch(addImagePosts(images))
        dispatch(addArticlePosts(articles))
        dispatch(addVideoPosts(videos))
        dispatch(addMultipostVideoFiles(videoFiles))
      }
      onContentAddedToComposer()
    }
  }

  const clearSelection = () => {
    dispatch(clearContentSelection())
  }

  const editImage = (imageUrl: string) => {
    dispatch(openComposerContentDialog({ tab: 'edit', imageUrl }))
  }

  const onContentClick = ({ imageUrl, videoUrl }: { imageUrl?: string, videoUrl?: string }) => {
    if (imageUrl || videoUrl) {
      setPreview({ imageUrl, videoUrl })
    }
  }

  const closePreview = () => {
    setPreview(null)
  }

  return (
    <Drawer
      variant="permanent"
      anchor="bottom"
      className={items.length === 0 ? styles.hidden : undefined}
      data-testid="content-selection-drawer"
      slotProps={{
        paper: {
          className: `${styles.drawer} ${open ? styles.open : ''} ${className || ''}`
        }
      }}
    >
      <div className={styles['btn-toggle']} onClick={toggleDrawer}>
        <IconToggle className={styles['icon-toggle']} />
      </div>
      <div className={styles.actions}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Icon size="18px" path={mdiFeather} />}
          onClick={addMultiPost}
        >
          <FormattedMessage
            id={hasMultiposts ? 'content.selection-panel.actions.multi-post-add' : 'content.selection-panel.actions.multi-post'}
            values={{ count: items.length }}
          />
        </Button>
        {withCarouselAction && (
          <Button
            startIcon={<Icon size="18px" path={mdiViewCarouselOutline} />}
            onClick={addCarouselPost}
          >
            <FormattedMessage id="content.selection-panel.actions.carousel" />
          </Button>
        )}
        {itemsOnPage.length > 1 && (
          <div
            role="button"
            className={`${styles['btn-select-all']} ${allSelected && styles.disabled}`}
            onClick={allSelected ? undefined : selectAll}
          >
            <FormattedMessage id="content.selection-panel.actions.select-all" values={{ count: itemsOnPage.length }} />
          </div>
        )}
        <div role="button" className={styles['btn-clear']} onClick={clearSelection}>
          <FormattedMessage id="content.selection-panel.actions.clear-all" />
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.list}>
          <CardList listHeight={LIST_HEIGHT}>
            {items.map(item => (
              <MiniContentCard
                key={item.id}
                content={item}
                thumbUrl={(item as any).thumbnailUrl} 
                onRemove={removeContent} 
                onEditImage={editImage}
                onClick={onContentClick}
              />
            ))}
          </CardList>
        </div>
      </div>
      <PPDialog
        open={Boolean(preview)}
        onClose={closePreview}
        title="Preview"
        className={styles['preview-paper']}
        data-testid="content-preview-dialog"
      >
        {preview?.videoUrl ? (
          <NativeVideoPlayer
            className={styles['preview-media']}
            videoUrl={preview.videoUrl}
            autoplay
            controls
            preload="metadata"
          />
        ) : preview?.imageUrl ? (
          <img className={styles['preview-media']} src={preview.imageUrl} />
        ) : null}
      </PPDialog>
    </Drawer>
  )
}

export default ContentSelectionDrawer
